/* =============================================================
   Atlavox Smart Filter — interaction layer

   Handles:
   - Click + drag set-mode multi-select
   - In-group search filtering
   - Info popover (clones <template> content, positions near trigger)

   Event delegation on document so handlers survive Dawn's
   facet-filters-form Section Rendering API refresh.
   ============================================================= */

(function () {
  'use strict';

  /* ---------- helpers ---------- */
  function getItem(node) {
    return node && node.closest ? node.closest('[data-atlavox-item]') : null;
  }
  function getCheckbox(item) {
    return item ? item.querySelector('input.atlavox-filter__checkbox') : null;
  }
  function getFacetForm(el) {
    var wrap = el && el.closest ? el.closest('facet-filters-form') : null;
    return wrap ? wrap.querySelector('form') : null;
  }

  /* =========================================================
     1) Click + drag set-mode multi-select
     ========================================================= */
  var dragState = null;

  function setItemState(item, checked) {
    var cb = getCheckbox(item);
    if (!cb || cb.disabled) return;
    if (cb.checked !== checked) cb.checked = checked;
    item.classList.toggle('is-active', checked);
  }

  function clearDragVisuals() {
    var items = document.querySelectorAll('.atlavox-filter__item.is-drag-target');
    for (var i = 0; i < items.length; i++) items[i].classList.remove('is-drag-target');
    var roots = document.querySelectorAll('.atlavox-facets[data-dragging]');
    for (var j = 0; j < roots.length; j++) roots[j].removeAttribute('data-dragging');
  }

  function commitForm(formEl) {
    if (!formEl) return;
    // facet-filters-form (Dawn's custom element) listens for input events
    // and debounces a Section Rendering API refresh.
    formEl.dispatchEvent(new Event('input', { bubbles: true }));
    scheduleLoadingWatchdog();
  }

  // Safety net: Dawn's facets.js silently swallows fetch errors via the
  // missing-.catch() chain. If renderProductCount / renderProductGridContainer
  // throw mid-flight (because some assumed-DOM element is missing), the
  // .loading class on .collection and the visible loading spinners never
  // clear. After every commit we set a watchdog that force-clears them so
  // the storefront isn't visually broken even if Dawn fails internally.
  function clearStuckLoadingState() {
    var spinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    for (var i = 0; i < spinners.length; i++) spinners[i].classList.add('hidden');
    var collection = document.querySelector('#ProductGridContainer .collection.loading');
    if (collection) collection.classList.remove('loading');
    var countContainers = document.querySelectorAll('#ProductCount.loading, #ProductCountDesktop.loading');
    for (var j = 0; j < countContainers.length; j++) countContainers[j].classList.remove('loading');
  }
  var loadingWatchdogTimer = null;
  function scheduleLoadingWatchdog() {
    if (loadingWatchdogTimer) clearTimeout(loadingWatchdogTimer);
    // Dawn debounces submit by 800ms, then network is typically <2s. Allow 5s.
    loadingWatchdogTimer = setTimeout(function () {
      var stillLoading = document.querySelector('#ProductGridContainer .collection.loading');
      if (stillLoading) {
        console.warn('[smart-filter] Loading state stuck — force clearing.');
        clearStuckLoadingState();
      }
    }, 5000);
  }

  // Suppress native label-click checkbox toggle — our pointer flow owns it
  document.addEventListener('click', function (e) {
    var lbl = e.target.closest('.atlavox-filter__value');
    if (lbl) {
      // Allow native behavior if it's a keyboard activation (no pointer drag in progress)
      // We only intercept mouse/touch label clicks because pointerdown/up already handled them.
      if (e.detail !== 0) e.preventDefault(); // detail === 0 means keyboard-triggered
    }
  });

  document.addEventListener('pointerdown', function (e) {
    if (e.button !== undefined && e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target.closest('[data-atlavox-info]')) return;
    if (e.target.closest('[data-atlavox-search]')) return;
    var item = getItem(e.target);
    if (!item) return;
    var cb = getCheckbox(item);
    if (!cb || cb.disabled) return;

    var mode = cb.checked ? 'remove' : 'add';
    var facets = item.closest('.atlavox-facets');
    if (facets) facets.setAttribute('data-dragging', mode);

    dragState = {
      mode: mode,
      traversed: new Set([item]),
      formEl: getFacetForm(item),
    };
    item.classList.add('is-drag-target');
    // Prevent text selection while dragging
    e.preventDefault();
  });

  // pointerover bubbles (unlike pointerenter) — use for delegation
  document.addEventListener('pointerover', function (e) {
    if (!dragState) return;
    var item = getItem(e.target);
    if (!item || dragState.traversed.has(item)) return;
    var cb = getCheckbox(item);
    if (!cb || cb.disabled) return;
    dragState.traversed.add(item);
    item.classList.add('is-drag-target');
  });

  function endDrag(commit) {
    if (!dragState) return;
    var state = dragState;
    dragState = null;
    if (commit) {
      var shouldCheck = state.mode === 'add';
      state.traversed.forEach(function (item) { setItemState(item, shouldCheck); });
      clearDragVisuals();
      if (state.formEl) commitForm(state.formEl);
    } else {
      clearDragVisuals();
    }
  }

  document.addEventListener('pointerup', function () { endDrag(true); });
  document.addEventListener('pointercancel', function () { endDrag(false); });
  window.addEventListener('blur', function () { endDrag(false); });

  // Keyboard sync — when checkbox state changes by keyboard, update the visual class
  document.addEventListener('change', function (e) {
    var cb = e.target.closest && e.target.closest('.atlavox-filter__checkbox');
    if (!cb) return;
    var item = getItem(cb);
    if (item) item.classList.toggle('is-active', cb.checked);
  });

  /* =========================================================
     2) In-group search filtering
     ========================================================= */
  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-atlavox-search]');
    if (!input) return;
    var filter = input.closest('.atlavox-filter');
    if (!filter) return;
    var q = input.value.trim().toLowerCase();
    var items = filter.querySelectorAll('[data-atlavox-item]');
    for (var i = 0; i < items.length; i++) {
      var text = items[i].getAttribute('data-search-text') || '';
      items[i].style.display = (!q || text.indexOf(q) !== -1) ? '' : 'none';
    }
  });

  /* =========================================================
     3) Info popover
     ========================================================= */
  var activePopover = null;
  var activeTrigger = null;

  function closePopover() {
    if (activePopover) {
      activePopover.remove();
      activePopover = null;
      activeTrigger = null;
    }
  }

  function positionPopover(popover, trigger) {
    var rect = trigger.getBoundingClientRect();
    var pw = popover.offsetWidth;
    var ph = popover.offsetHeight;
    var pad = 8;
    var left = rect.right + pad;
    var top = rect.top;
    // Flip to left side if overflowing right
    if (left + pw > window.innerWidth - 10) {
      left = rect.left - pw - pad;
      if (left < 10) left = Math.max(10, (window.innerWidth - pw) / 2);
    }
    // Clamp vertically
    if (top + ph > window.innerHeight - 10) {
      top = Math.max(10, window.innerHeight - ph - 10);
    }
    if (top < 10) top = 10;
    popover.style.left = left + 'px';
    popover.style.top = top + 'px';
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-atlavox-info]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      // Toggle off if clicking the same trigger again
      if (activeTrigger === trigger) { closePopover(); return; }
      closePopover();
      var tplId = trigger.getAttribute('data-popover-target');
      var tpl = document.getElementById(tplId);
      if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;
      var node = tpl.content.firstElementChild.cloneNode(true);
      document.body.appendChild(node);
      activePopover = node;
      activeTrigger = trigger;
      // Position after layout
      requestAnimationFrame(function () { positionPopover(node, trigger); });
      return;
    }
    if (e.target.closest('[data-atlavox-popover-close]')) {
      closePopover();
      return;
    }
    if (activePopover && !e.target.closest('.atlavox-popover')) {
      closePopover();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closePopover();
  });
  window.addEventListener('scroll', closePopover, { passive: true });
  window.addEventListener('resize', closePopover);

  /* =========================================================
     4) Mobile drawer open/close
     ========================================================= */
  function openMobileDrawer() {
    var container = document.querySelector('[data-atlavox-facets-container]');
    if (!container) return;
    container.classList.add('is-open');
    document.body.classList.add('atlavox-facets-locked');
  }
  function closeMobileDrawer() {
    var openContainer = document.querySelector('[data-atlavox-facets-container].is-open');
    if (!openContainer) return;
    openContainer.classList.remove('is-open');
    document.body.classList.remove('atlavox-facets-locked');
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-atlavox-open]')) {
      e.preventDefault();
      openMobileDrawer();
      return;
    }
    if (e.target.closest('[data-atlavox-close]')) {
      e.preventDefault();
      closeMobileDrawer();
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeMobileDrawer();
  });

  /* =========================================================
     5) Mobile trigger — stick on scroll
     ---------------------------------------------------------
     position: sticky is silently defeated when ANY ancestor has
     overflow != visible (which Dawn / installed apps often set on
     mobile to prevent horizontal scroll). Drive the stick with JS
     instead: when the placeholder's top edge passes the header's
     bottom edge, toggle .is-stuck on the trigger and .is-active on
     the placeholder. Both states converge cleanly so the product
     grid doesn't jump as the trigger detaches into fixed flow.
     ========================================================= */
  function getHeaderHeightPx() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var n = parseInt(v, 10);
    return isNaN(n) ? 64 : n;
  }
  // Debug switch — flip to true to log the stick math in the console.
  var DBG_STICK = false;

  var stickCheckPending = false;

  // State-machine version:
  //   - When NOT stuck, measure the BUTTON's getBoundingClientRect().top
  //     directly. Stick when it crosses above the header bottom.
  //   - When stuck, measure the PLACEHOLDER's rect.top to know when to
  //     unstick (placeholder lives at the button's natural Y while
  //     stuck, with .is-active giving it height to reserve flow space).
  //
  // This avoids relying on the placeholder having a layout position
  // at page load — the button is the element we can always measure
  // because the user can see it (so it isn't display:none).
  function checkMobileTriggerStick() {
    var trigger = document.querySelector('.atlavox-facets__mobile-trigger');
    var placeholder = document.querySelector('.atlavox-facets__mobile-trigger-placeholder');
    if (!trigger || !placeholder) return;
    var headerH = getHeaderHeightPx();
    var isStuck = trigger.classList.contains('is-stuck');
    var measured, source, rect;
    if (isStuck) {
      rect = placeholder.getBoundingClientRect();
      measured = rect.top;
      source = 'placeholder';
    } else {
      rect = trigger.getBoundingClientRect();
      measured = rect.top;
      source = 'trigger';
    }
    var shouldStick = measured < headerH;
    if (DBG_STICK) {
      var phStyle = window.getComputedStyle(placeholder);
      console.log('[atlavox stick]',
        'isStuck=', isStuck,
        'source=', source,
        'top=', measured,
        'rect=', JSON.stringify({top: rect.top, height: rect.height, width: rect.width}),
        'placeholder.display=', phStyle.display,
        'placeholder.height=', phStyle.height,
        'headerH=', headerH,
        'shouldStick=', shouldStick);
    }
    trigger.classList.toggle('is-stuck', shouldStick);
    placeholder.classList.toggle('is-active', shouldStick);
  }
  function scheduleStickCheck() {
    if (stickCheckPending) return;
    stickCheckPending = true;
    window.requestAnimationFrame(function () {
      stickCheckPending = false;
      checkMobileTriggerStick();
    });
  }
  window.addEventListener('scroll', scheduleStickCheck, { passive: true });
  window.addEventListener('resize', scheduleStickCheck);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkMobileTriggerStick);
  } else {
    checkMobileTriggerStick();
  }

  /* =========================================================
     6) Active filter count on mobile trigger badge
     ========================================================= */
  function updateMobileTriggerCount() {
    var badge = document.querySelector('[data-atlavox-active-count]');
    if (!badge) return;
    var count = document.querySelectorAll('.active-facets-desktop facet-remove').length;
    badge.textContent = count;
  }
  // Initial run + watch for changes from Dawn's renderActiveFacets
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateMobileTriggerCount);
  } else {
    updateMobileTriggerCount();
  }
  var pillsContainer = document.querySelector('.active-facets-desktop');
  if (pillsContainer && 'MutationObserver' in window) {
    new MutationObserver(updateMobileTriggerCount).observe(pillsContainer, {
      childList: true,
      subtree: true,
    });
  }
})();
